import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');
  const contentId = searchParams.get('content_id');

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        }
        if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
        if (attempts < 5) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-texture flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="ch-card p-8 max-w-md w-full text-center animate-fade-in-up">
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="text-[--ch-cyan] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
            <p className="text-sm text-[--ch-text-secondary]">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={48} className="text-[--ch-success] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" data-testid="payment-success-title">Payment Successful!</h2>
            <p className="text-sm text-[--ch-text-secondary] mb-6">Your purchase has been confirmed.</p>
            <div className="flex gap-3 justify-center">
              {contentId ? (
                <button data-testid="view-content-btn" onClick={() => navigate('/gallery')} className="btn-neon px-6 py-2.5 rounded-lg text-sm">
                  View Content
                </button>
              ) : (
                <button data-testid="browse-content-btn" onClick={() => navigate('/gallery')} className="btn-neon px-6 py-2.5 rounded-lg text-sm">
                  Browse Content
                </button>
              )}
            </div>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle size={48} className="text-[--ch-error] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" data-testid="payment-error-title">Payment Issue</h2>
            <p className="text-sm text-[--ch-text-secondary] mb-6">
              {status === 'expired' ? 'Payment session expired.' : 'Unable to verify payment. Please check your email for confirmation.'}
            </p>
            <button data-testid="try-again-btn" onClick={() => navigate('/subscribe')} className="btn-outline-neon px-6 py-2.5 rounded-lg text-sm">
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
